import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert, MenuItem } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ExportMenu } from '../../components/ExportMenu';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { SiteFormDialog } from './SiteFormDialog';
import { sitesApi, type Site, type CreateSiteInput, type SiteStatus } from '../../api/sites.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const STATUS_COLOR: Record<SiteStatus, 'success' | 'default' | 'info'> = {
  active: 'success', inactive: 'default', completed: 'info', closed: 'default',
};

export function SiteListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [zone, setZone] = useState('');
  const [priority, setPriority] = useState('');
  const [minHandoverDelay, setMinHandoverDelay] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);
  const [deleting, setDeleting] = useState<Site | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sites', page, limit, search, city, zone, priority, minHandoverDelay],
    queryFn: () => sitesApi.list({ page, limit, search: search || undefined, city: city || undefined, zone: zone || undefined, priority: priority || undefined, minHandoverDelay: minHandoverDelay || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['sites'] });

  const createMut = useMutation({
    mutationFn: (input: CreateSiteInput) => sitesApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateSiteInput }) => {
      const { projectId: _projectId, ...rest } = input;
      return sitesApi.update(id, rest);
    },
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => sitesApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Site>[] = [
    { key: 'siteCode', label: 'Code', width: 110 },
    { key: 'siteName', label: 'Name' },
    { key: 'projectId', label: 'Project ID', width: 100 },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={STATUS_COLOR[r.status]} /> },
  ];

  return (
    <>
      <PageHeader
        title="Site Master"
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="sites" permission="site.export" filters={{ search, city, zone, priority, minHandoverDelay }} />
            {hasPermission('site.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Site</Button>}
          </Stack>
        }
      />
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField size="small" placeholder="Search name / code / city" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <TextField size="small" label="City" value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }} sx={{ minWidth: 120 }} />
        <TextField size="small" label="Zone" value={zone} onChange={(e) => { setZone(e.target.value); setPage(1); }} sx={{ minWidth: 120 }} />
        <TextField size="small" select label="Priority" value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {['low', 'medium', 'high', 'critical'].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <TextField size="small" type="number" label="Min handover delay (days)" value={minHandoverDelay} onChange={(e) => { setMinHandoverDelay(e.target.value); setPage(1); }} sx={{ minWidth: 200 }} />
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('site.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('site.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <SiteFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete site"
        message={`Soft-delete ${deleting?.siteName}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
